import { INotificationUnion, NotificationRecipientExtrases } from "@elit-hair/elitklinik-shared";
import { Avatar, ListItemAvatar, ListItemButton, ListItemText, SxProps, Theme, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { createElement } from "react";
import { TbProgress } from "react-icons/tb";
import { NavLink } from "react-router-dom";

import { softColors } from "@/constants/colors";
import { checkBit } from "@/helpers/bitwise";
import { useMarkNotificationAsRead } from "@/requests/notification.queries";
import { NotificationCategories } from "@/types/global";

import NotificationChanges from "./NotificationChanges";

/**
 * Wrapper component for notification items that have navigation links.
 * Renders as a React Router NavLink when notification has a navigateTo URL.
 */
function NotificationItemLink({
    notification,
    children,
    handleClick,
    className,
    sx,
}: {
    notification: INotificationUnion;
    children: React.ReactNode;
    handleClick: () => void;
    className?: string;
    sx?: SxProps<Theme>;
}) {
    let link = (notification.notification?.navigateTo || "").replace("#/", "");
    if (!link.startsWith("/")) link = "/" + link;

    return (
        <ListItemButton onClick={handleClick} component={NavLink} to={link} className={className + " relative"} sx={sx}>
            {children}
        </ListItemButton>
    );
}

/**
 * Content component for notification items displaying title, body, timestamp and read status.
 */
function NotificationContent({ notification, isRead }: { notification: INotificationUnion; isRead: boolean }) {
    const createdAt = DateTime.fromISO(notification.createdAt);
    const relativeTime = createdAt.toRelative({ locale: "tr" });

    return (
        <>
            <ListItemAvatar>
                <Avatar className="!absolute !top-4" sx={{ backgroundColor: softColors.indigo.hover }}>
                    {createElement(TbProgress, {
                        fontSize: 20,
                    })}
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <div className="flex flex-col  ">
                        <div className="flex text-xs    items-center gap-1 justify-center"></div>
                        <div className=" flex justify-between items-center font-medium gap-1">
                            <div
                                className="text-black "
                                dangerouslySetInnerHTML={{ __html: notification.notification.title || "" }}
                            ></div>
                            <span className="text-gray-500 text-xs relative flex justify-center">
                                <Tooltip title={createdAt.setLocale("tr").toFormat("dd MMMM yyyy, HH:mm")}>
                                    <span>{relativeTime}</span>
                                </Tooltip>
                                {!isRead && (
                                    <div className="w-2 h-2 bg-indigo-500 rounded-full absolute top-[140%]"></div>
                                )}
                            </span>
                        </div>
                    </div>
                }
                secondary={
                    <span className=" !pr-9 !text-sm block ">
                        <div dangerouslySetInnerHTML={{ __html: notification.notification.body }}></div>
                        <NotificationChanges changes={notification.context.changes} category={notification.category} />
                    </span>
                }
            />
        </>
    );
}

const listItemButtonSx = {
    "&:hover": {
        backgroundColor: `${softColors.grey.class} !important`,
    },
};

/**
 * Main notification item component. Handles click events, read status updates,
 * and renders either as a navigatable link or regular button based on notification type.
 */
function NotificationItem({
    notification,
    category,
    handleClose,
}: {
    notification: INotificationUnion;
    category?: NotificationCategories;
    handleClose: () => void;
}) {
    const isRead = checkBit(NotificationRecipientExtrases.READ, notification.recipient.extras);
    const { mutate: markNotificationsAsRead, isPending } = useMarkNotificationAsRead({ category });
    const handleClick = () => {
        if (!isRead && !isPending) {
            markNotificationsAsRead([notification.recipient.id]);
        }
        if (notification.notification.navigateTo) handleClose();
    };

    const className = " !duration-200 " + ((!isRead && "!bg-blue-50") || "");

    if (notification.notification.navigateTo)
        return (
            <NotificationItemLink
                sx={listItemButtonSx}
                notification={notification}
                className={className}
                handleClick={handleClick}
            >
                <NotificationContent isRead={isRead} notification={notification} />
            </NotificationItemLink>
        );
    else
        return (
            <ListItemButton onClick={handleClick} className={className} sx={listItemButtonSx}>
                <NotificationContent isRead={isRead} notification={notification} />
            </ListItemButton>
        );
}

export default NotificationItem;
